import { defineComponent, h, useAttrs } from 'vue'

let assetContext = null

try {
  assetContext = require.context(
    '@/assets',
    false,
    /\.(png|jpe?g|svg|gif|webp)$/
  )
} catch {
  assetContext = null
}

function hasBooleanishAttr(value) {
  return value === '' || value === true || value === 'true'
}

function normalizeImageAttrs(attrs) {
  const normalized = { ...attrs }
  delete normalized.contain
  delete normalized.eager
  delete normalized.cover
  delete normalized.src
  delete normalized.width
  delete normalized.height
  return normalized
}

function normalizeSizeValue(value) {
  if (value == null || value === '') return undefined
  if (typeof value === 'number') return `${value}px`
  return /^\d+(\.\d+)?$/.test(String(value)) ? `${value}px` : value
}

function normalizeImageStyle(attrs) {
  const width = normalizeSizeValue(attrs.width)
  const height = normalizeSizeValue(attrs.height)

  return [
    attrs.style,
    {
      display: 'block',
      // `max-width: 100%` used to be hardcoded here, but an inline style
      // always wins over an author-supplied class (e.g. a page's own
      // `.p-mobile-image { max-width: 100px }` breakpoint rules) regardless
      // of that class's specificity - so it silently overrode every
      // consumer's own sizing everywhere in the app. A bare block-level div
      // already defaults to filling its parent's width, so this was
      // redundant for the no-width case and actively wrong for the
      // class-sized case. Master's real Vuetify 2 `v-img` never does this;
      // it lets author CSS fully control sizing.
      ...(width ? { width } : {}),
      ...(height ? { height } : {})
    }
  ]
}

function resolveModuleSrc(asset) {
  if (!asset || typeof asset !== 'object') return asset
  return asset.default || asset.src || asset
}

function resolveAssetAlias(src) {
  if (typeof src !== 'string' || !src.startsWith('@/assets/')) return src

  const assetPath = `./${src.replace('@/assets/', '')}`

  try {
    if (!assetContext) return src

    return resolveModuleSrc(assetContext(assetPath))
  } catch {
    return src
  }
}

function resolveSrc(src) {
  const resolved = resolveAssetAlias(resolveModuleSrc(src))
  return typeof resolved === 'string' ? resolved.trim() : resolved
}

export default defineComponent({
  name: 'VImgCompat',
  compatConfig: {
    RENDER_FUNCTION: false,
    COMPONENT_V_MODEL: false,
    INSTANCE_ATTRS_CLASS_STYLE: false
  },
  inheritAttrs: false,

  setup(_, { slots }) {
    const attrs = useAttrs()

    return () => {
      const normalizedAttrs = normalizeImageAttrs(attrs)
      const src = resolveSrc(attrs.src)
      const objectFit = hasBooleanishAttr(attrs.contain) ? 'contain' : 'cover'
      const imagePosition = attrs.position || 'center center'

      if (!src) {
        return h('div', {
          ...normalizedAttrs,
          class: ['v-image', 'v-responsive', normalizedAttrs.class],
          style: normalizeImageStyle(attrs),
          role: normalizedAttrs.alt ? 'img' : undefined,
          'aria-label': normalizedAttrs.alt
        })
      }

      return h(
        'div',
        {
          ...normalizedAttrs,
          class: ['v-image', 'v-responsive', normalizedAttrs.class],
          style: normalizeImageStyle(attrs),
          role: normalizedAttrs.alt ? 'img' : undefined,
          'aria-label': normalizedAttrs.alt
        },
        [
          h('img', {
            class: ['v-image__image', `v-image__image--${objectFit}`],
            src,
            alt: normalizedAttrs.alt || '',
            style: {
              display: 'block',
              height: '100%',
              objectFit,
              objectPosition: imagePosition,
              width: '100%'
            }
          })
        ]
      )
    }
  }
})
